/**
 * Разрешението „достъп до локалната мрежа" — без обиколка на устройствата.
 *
 * Chromium пуска заявка от HTTPS страница към частен адрес (касата) само с
 * разрешение, а разрешението се иска САМО при действие на човек. Сондата при
 * вход не е действие на човек, затова досега всяко устройство искаше отделно
 * посещение: бутонът „Подготви това устройство" или скриптът `edge_policy.ps1`
 * — а скрипт на телефона на сервитьора не се пуска, и при 10 точки това е 10
 * пъти една и съща обиколка.
 *
 * Тук е другият път: щом сондата каже „не стигам до касата", закачаме ЕДНО
 * слушане за първото докосване по екрана и пускаме сондата пак — вече в
 * рамките на човешко действие, тоест с подканата. Операторът вижда въпроса на
 * браузъра по време на обичайната работа и натиска „Разреши"; устройството се
 * подготвя само, без никой да ходи до него.
 *
 * Понеже отказът е по-лош от липсата на разрешение (втора подкана не идва),
 * заедно с подканата излиза и ред на български какво се пита — но само когато
 * браузърът потвърди, че наистина ще пита (Permissions API). Без този отговор
 * мълчим: собственият текст на Chrome е по-добър от наш надпис за подкана,
 * която може изобщо да не се появи (изгасена каса).
 */
import i18n from "../../translations/i18n";
import { showGlobalInfoToast } from "../messageBridge";
import { devLog } from "./edgeRuntime";
import {
  isReachabilityProbeRunning,
  probeEdgeReachability,
} from "./edgeSession";
import {
  getEdgeBaseUrl,
  getEdgeConfigState,
  isEdgeLocalMode,
} from "./edgeState";

/** Между два опита при докосване: неуспелият опит е една заявка с 3 s таван,
 * а касата може просто да е изгасена — няма смисъл от сонда на всеки клик. */
const RETRY_AFTER_MS = 10 * 60_000;
/** Колкото да се прочете, докато подканата стои на екрана. */
const TOAST_SECONDS = 8;
const listenerOptions = { capture: true, passive: true } as const;

type PermissionState = "granted" | "prompt" | "denied" | "unknown";

let armed = false;
let lastAttemptAt = 0;
let rearmTimer: number | null = null;
let explained = false;

/**
 * Резултатът от всяка сонда към касата минава оттук (`edgeSession.ts`).
 * Успех → нямаме работа. Провал в облачен режим → чакаме първото докосване.
 */
export function noteEdgeProbeResult(reachable: boolean): void {
  if (reachable) {
    disarm();
    return;
  }
  arm();
}

function arm(): void {
  if (armed || typeof window === "undefined") return;
  if (!getEdgeConfigState()?.enabled || !getEdgeBaseUrl()) return;
  // В локален режим устройството очевидно стига до касата.
  if (isEdgeLocalMode()) return;
  if (Date.now() - lastAttemptAt < RETRY_AFTER_MS) return;
  armed = true;
  window.addEventListener("pointerdown", onGesture, listenerOptions);
  window.addEventListener("keydown", onGesture, listenerOptions);
}

function disarm(): void {
  if (!armed || typeof window === "undefined") return;
  armed = false;
  window.removeEventListener("pointerdown", onGesture, listenerOptions);
  window.removeEventListener("keydown", onGesture, listenerOptions);
}

function onGesture(): void {
  // Сонда вече лети (хистерезисът на `edgeMode.ts` я е пуснал) — тя е тръгнала
  // ИЗВЪН човешко действие и подкана няма да вдигне, а `probeEdgeReachability`
  // би върнал нея. Оставаме закачени за следващото докосване.
  if (isReachabilityProbeRunning()) return;
  disarm();
  lastAttemptAt = Date.now();
  // Заявката тръгва ВЕДНАГА, в същия tick — активирането от човека важи
  // няколко секунди и всяко `await` преди нея е риск да го изпуснем.
  // Без `humanAction`: това докосване е на всеки терминал в залата, а
  // `127.0.0.1` се пита само от „Подготви"/„Провери" (`edgeLoopback.ts`).
  const probe = probeEdgeReachability();
  void explainIfBrowserWillAsk();
  void probe.then((reachable) => {
    if (!reachable) scheduleRearm();
  });
}

/** Касата може да е била изгасена точно тогава: след паузата пак чакаме
 * докосване, без да зависим от това дали нещо друго ще пусне сонда. */
function scheduleRearm(): void {
  if (rearmTimer !== null || typeof window === "undefined") return;
  rearmTimer = window.setTimeout(() => {
    rearmTimer = null;
    arm();
  }, RETRY_AFTER_MS);
}

/**
 * Един ред на екрана, ако браузърът ще пита (или вече е отказал). Състоянието
 * се чете от Permissions API — името е на Chromium и на друг браузър просто
 * гърми; тогава мълчим.
 */
async function explainIfBrowserWillAsk(): Promise<void> {
  if (explained) return;
  const state = await localNetworkPermissionState();
  if (state === "granted" || state === "unknown") return;
  explained = true;
  showGlobalInfoToast(
    i18n.t(state === "denied" ? "edge.lnaDenied" : "edge.lnaPrompt"),
    TOAST_SECONDS,
  );
}

/** Състоянието на разрешението според браузъра. Чете го и „Подготви това
 * устройство" (`edgeSession.ts`): „отказа достъп" се казва САМО при `denied`. */
export async function localNetworkPermissionState(): Promise<PermissionState> {
  try {
    const status = await navigator.permissions.query({
      name: "local-network-access",
    } as unknown as PermissionDescriptor);
    return status.state as PermissionState;
  } catch (error) {
    devLog("local-network-access permission unknown", error);
    return "unknown";
  }
}
